/**
 * Sync tolerances, timings, and platform limits.
 *
 * These constants are FROZEN once WS-0 merges. Changing a value requires an
 * integration request (see the master build prompt §11.4). Do not redefine
 * these anywhere else in the codebase.
 */
/** Two-tier playback-correction tuning (server-authoritative sync). */
export const SYNC = {
    /** |drift| below this band -> do nothing (avoid jitter from noise). */
    IGNORE_BAND_S: 0.3,
    /** Below this -> playbackRate nudge (soft sync). */
    SOFT_SYNC_MAX_S: 2.0,
    /** 1.05x / 0.95x nudge delta. */
    SOFT_RATE_DELTA: 0.05,
    /** Duration of a nudge before reverting to the base rate. */
    SOFT_WINDOW_MS: 1500,
    /** Above this -> hard seek via currentTime(). */
    HARD_SEEK_ABOVE_S: 2.0,
    /** Server -> clients heartbeat cadence while playing. */
    HEARTBEAT_MS: 2000,
    /** Clock-offset sampling interval. */
    PING_INTERVAL_MS: 5000,
    /** Number of ping samples retained; min-RTT sample is used (Jellyfin SyncPlay style). */
    PING_SAMPLES: 8,
    /** Viewer seek-reversion tolerance. */
    SEEK_GUARD_TOLERANCE_S: 0.5,
    /** Client shows "reconnecting" beyond this with no sync:apply. */
    STALE_STATE_MS: 15000,
};
/** Platform limits enforced server-side. */
export const LIMITS = {
    ROOM_NAME_MAX: 60,
    DISPLAY_NAME_MAX: 32,
    MEMBERS_PER_ROOM_MAX: 25,
    ROOMS_MAX: 100,
    /** Per-socket event budget over 10 s (sync:ping exempt). */
    SOCKET_EVENTS_PER_10S: 60,
    /** Subtitle delay slider range, ±seconds. */
    SUBTITLE_OFFSET_RANGE_S: 60,
    /** Audio delay ceiling, milliseconds (positive-only). */
    AUDIO_DELAY_MAX_MS: 60_000,
    /** Max chat message length. */
    CHAT_MESSAGE_MAX: 500,
    /** Chat rate limit: max messages per window. */
    CHAT_RATE_MAX: 6,
    /** Chat rate limit window (ms). */
    CHAT_RATE_WINDOW_MS: 20_000,
    /** Chat rate-limit cooldown (ms) after exceeding the limit. */
    CHAT_RATE_COOLDOWN_MS: 30_000,
};
/** Allowed playback rates (host sync:rate must be one of these). */
export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];
/** Rendition ladder targets keyed by height. */
export const RENDITION_TARGETS = {
    1080: { width: 1920, height: 1080, bitrate: 5_000_000 },
    720: { width: 1280, height: 720, bitrate: 2_800_000 },
    480: { width: 854, height: 480, bitrate: 1_400_000 },
};
/** Ordered ladder heights (high -> low), filtered by RENDITION_LADDER env. */
export const RENDITION_LADDER_ORDER = [1080, 720, 480];
/** Session JWT lifetime (12 hours, in seconds). */
export const SESSION_TTL_SEC = 12 * 60 * 60;
/** Media access cookie lifetime (30 minutes, in seconds). */
export const MEDIA_COOKIE_TTL_SEC = 30 * 60;
/** Media cookie refresh cadence on the client (20 minutes, in ms). */
export const MEDIA_COOKIE_REFRESH_MS = 20 * 60 * 1000;
/** Room reaper: delete rooms with 0 members for longer than this. */
export const ROOM_REAPER_MS = 30 * 60 * 1000;
//# sourceMappingURL=constants.js.map